// src/pages/PropuestasIncidencias.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    where,
    getDocs,
    updateDoc,
} from 'firebase/firestore';
import {
    FaCamera,
    FaEnvelope,
    FaEnvelopeOpen,
    FaHandsHelping,
    FaMapMarkerAlt,
    FaMicrophone,
    FaPlus,
    FaTimes,
    FaUser,
    FaVideo,
} from 'react-icons/fa';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import './PropuestasIncidencias.css';
import './PropuestasIncidenciasMobile.css';

const FILTERS = [
    { id: 'todas', label: 'Todas' },
    { id: 'incidencias', label: 'Incidencias' },
    { id: 'propuestas', label: 'Propuestas' },
    { id: 'mias', label: 'Mis reportes' },
];

const TYPE_META = {
    incidencia: { label: 'Incidencia', className: 'tag-incidencia' },
    propuesta: { label: 'Propuesta', className: 'tag-propuesta' },
};

const STATUS_META = {
    pendiente: { label: 'Pendiente', className: 'status-pending' },
    'en-proceso': { label: 'En proceso', className: 'status-progress' },
    resuelta: { label: 'Resuelta', className: 'status-resuelta' },
};

const ATTACHMENT_META = {
    foto: { icon: <FaCamera />, label: 'Foto' },
    video: { icon: <FaVideo />, label: 'Video' },
    audio: { icon: <FaMicrophone />, label: 'Audio' },
};

const PropuestasIncidencias = () => {
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();
    const { showNotification } = useNotification();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    const [reportes, setReportes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('todas');
    const [readStatus, setReadStatus] = useState({});
    const [toggleLoading, setToggleLoading] = useState({});
    const [evidenciasModal, setEvidenciasModal] = useState({ open: false, reporte: null });

    const uidActual = currentUser?.uid || userData?.uid || null;

    useEffect(() => {
        if (!fraccionamientoId) {
            setReportes([]);
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        const reportesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'propuestasIncidencias');
        const q = query(reportesRef, orderBy('fechaCreacion', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const lista = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
            setReportes(lista);
            setLoading(false);
        }, (error) => {
            console.error('Error al obtener propuestas e incidencias: ', error);
            setReportes([]);
            setLoading(false);
        });

        return unsubscribe;
    }, [fraccionamientoId]);

    const reportesFiltrados = useMemo(() => {
        const normalizarTipo = (valor = '') => String(valor).toLowerCase();

        return reportes.filter((reporte) => {
            const tipo = normalizarTipo(reporte.tipo);
            const creadorId = reporte.creadorId || reporte.uid;

            if (activeFilter === 'incidencias') {
                return tipo === 'incidencia';
            }
            if (activeFilter === 'propuestas') {
                return tipo === 'propuesta';
            }
            if (activeFilter === 'mias') {
                if (!uidActual) return false;
                return uidActual === creadorId;
            }
            // Default: todas
            return true;
        });
    }, [activeFilter, reportes, uidActual]);

    useEffect(() => {
        if (!fraccionamientoId || !uidActual) {
            setReadStatus({});
            return;
        }

        if (!reportes.length) {
            setReadStatus({});
            return;
        }

        let cancelled = false;

        const fetchLecturas = async () => {
            try {
                const entries = await Promise.all(reportes.map(async (reporte) => {
                    const lecturaRef = doc(
                        db,
                        'fraccionamientos',
                        fraccionamientoId,
                        'propuestasIncidencias',
                        reporte.id,
                        'lecturas',
                        uidActual,
                    );
                    const lecturaSnap = await getDoc(lecturaRef);
                    return [reporte.id, lecturaSnap.exists()];
                }));
                if (!cancelled) {
                    setReadStatus(Object.fromEntries(entries));
                }
            } catch (error) {
                console.error('Error al obtener lecturas de propuestas/incidencias', error);
                if (!cancelled) {
                    setReadStatus({});
                }
            }
        };

        fetchLecturas();

        return () => {
            cancelled = true;
        };
    }, [fraccionamientoId, uidActual, reportes]);

    const handleToggleLectura = async (reporteId, marcadoComoLeido) => {
        if (!fraccionamientoId || !uidActual) {
            showNotification('No se pudo actualizar el estado de lectura.', 'error');
            return;
        }

        const lecturaRef = doc(
            db,
            'fraccionamientos',
            fraccionamientoId,
            'propuestasIncidencias',
            reporteId,
            'lecturas',
            uidActual,
        );

        setToggleLoading((prev) => ({ ...prev, [reporteId]: true }));

        try {
            if (marcadoComoLeido) {
                await deleteDoc(lecturaRef);
                setReadStatus((prev) => ({ ...prev, [reporteId]: false }));
                showNotification('Reporte marcado como no leído.', 'info');

                // Sincronizar con la campana de notificaciones (marcar como NO leída)
                const notifRef = collection(db, 'usuarios', uidActual, 'notificaciones');
                const q = query(notifRef, where('reportId', '==', reporteId));
                const notifSnap = await getDocs(q);
                if (!notifSnap.empty) {
                    await Promise.all(notifSnap.docs.map(nDoc => updateDoc(nDoc.ref, { leida: false })));
                }
            } else {
                await setDoc(lecturaRef, {
                    leidoEn: serverTimestamp(),
                    usuarioId: uidActual,
                    usuarioNombre: userData?.nombre || userData?.nombreCompleto || currentUser?.displayName || null,
                    usuarioEmail: userData?.email || currentUser?.email || null,
                }, { merge: true });
                setReadStatus((prev) => ({ ...prev, [reporteId]: true }));
                showNotification('Reporte marcado como leído.', 'success');

                // Sincronizar con la campana de notificaciones (marcar como leída)
                const notifRef = collection(db, 'usuarios', uidActual, 'notificaciones');
                const q = query(notifRef, where('reportId', '==', reporteId));
                const notifSnap = await getDocs(q);
                if (!notifSnap.empty) {
                    await Promise.all(notifSnap.docs.map(nDoc => updateDoc(nDoc.ref, { leida: true })));
                }
            }
        } catch (error) {
            console.error('Error al actualizar lectura del reporte', error);
            showNotification('No se pudo actualizar el estado de lectura. Inténtalo más tarde.', 'error');
        } finally {
            setToggleLoading((prev) => ({ ...prev, [reporteId]: false }));
        }
    };

    const obtenerEvidencias = (reporte) => {
        const evidencias = { fotos: [], videos: [], audios: [] };
        const adjuntos = reporte.adjuntos;

        if (adjuntos && typeof adjuntos === 'object') {
            if (adjuntos.fotos && Array.isArray(adjuntos.fotos)) {
                evidencias.fotos = adjuntos.fotos.map((foto) => foto.url || foto);
            }
            if (adjuntos.videos && Array.isArray(adjuntos.videos)) {
                evidencias.videos = adjuntos.videos.map((video) => video.url || video);
            }
            if (adjuntos.audios && Array.isArray(adjuntos.audios)) {
                evidencias.audios = adjuntos.audios.map((audio) => audio.url || audio);
            }
        }

        // Compatibilidad con formato antiguo
        if (reporte.fotoUrl) evidencias.fotos.push(reporte.fotoUrl);
        if (reporte.videoUrl) evidencias.videos.push(reporte.videoUrl);
        if (reporte.audioUrl) evidencias.audios.push(reporte.audioUrl);
        if (reporte.fotos && Array.isArray(reporte.fotos)) {
            evidencias.fotos.push(...reporte.fotos.map((f) => (typeof f === 'string' ? f : f.url)));
        }
        if (reporte.videos && Array.isArray(reporte.videos)) {
            evidencias.videos.push(...reporte.videos.map((v) => (typeof v === 'string' ? v : v.url)));
        }
        if (reporte.audios && Array.isArray(reporte.audios)) {
            evidencias.audios.push(...reporte.audios.map((a) => (typeof a === 'string' ? a : a.url)));
        }

        return evidencias;
    };

    const handleVerEvidencias = (reporte) => {
        const evidencias = obtenerEvidencias(reporte);
        const tieneEvidencias = evidencias.fotos.length > 0 || evidencias.videos.length > 0 || evidencias.audios.length > 0;

        if (tieneEvidencias) {
            setEvidenciasModal({ open: true, reporte, evidencias });
        }
    };

    const cerrarModalEvidencias = () => {
        setEvidenciasModal({ open: false, reporte: null, evidencias: null });
    };

    const renderAdjuntos = (reporte) => {
        const flags = extraerAdjuntos(reporte);
        const keys = Object.keys(flags).filter((key) => flags[key]);

        if (!keys.length) {
            return null;
        }

        return (
            <div className="reporte-attachments">
                {keys.map((key) => (
                    <button
                        key={key}
                        type="button"
                        className={`attachment-pill attachment-${key}`}
                        title={`Ver ${ATTACHMENT_META[key]?.label}`}
                        onClick={() => handleVerEvidencias(reporte)}
                    >
                        {ATTACHMENT_META[key]?.icon}
                    </button>
                ))}
            </div>
        );
    };

    const tituloFiltroActivo = FILTERS.find((filter) => filter.id === activeFilter)?.label ?? 'Todas';

    return (
        <div className="reportes-page">
            <div className="reportes-header">
                <div className="header-main">
                    <div className="header-icon">
                        <FaHandsHelping />
                    </div>
                    <div>
                        <h1>Propuestas e Incidencias</h1>
                        <p>Da seguimiento a las ideas y reportes de tu comunidad. Filtra por tipo, estado o revisa tus propios reportes.</p>
                    </div>
                </div>
                <button
                    type="button"
                    className="header-cta"
                    onClick={() => navigate('/comunidad/propuestas/nueva')}
                >
                    <FaPlus />
                    Crear incidencia
                </button>
            </div>

            <div className="reportes-filters">
                {FILTERS.map((filter) => (
                    <button
                        key={filter.id}
                        type="button"
                        className={`filter-pill ${activeFilter === filter.id ? 'active' : ''}`}
                        onClick={() => setActiveFilter(filter.id)}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="loading-message">Cargando reportes...</div>
            ) : reportesFiltrados.length === 0 ? (
                <div className="empty-state-reportes">
                    <h3>No hay registros en "{tituloFiltroActivo}"</h3>
                    <p>Cuando existan propuestas o incidencias, aparecerán aquí con su estado actual.</p>
                </div>
            ) : (
                <div className="reportes-list">
                    {reportesFiltrados.map((reporte) => {
                        const tipoKey = String(reporte.tipo || '').toLowerCase();
                        const estadoKey = String(reporte.estado || '').toLowerCase().replace(/\s+/g, '-');
                        const tipoMeta = TYPE_META[tipoKey] || { label: reporte.tipo || 'Reporte', className: 'tag-generic' };
                        const estadoMeta = STATUS_META[estadoKey] || { label: reporte.estado || 'Sin estado', className: 'status-default' };
                        const estaLeido = Boolean(readStatus[reporte.id]);
                        const procesando = Boolean(toggleLoading[reporte.id]);
                        const cardClassName = `reporte-card${estaLeido ? '' : ' reporte-unread'}`;

                        return (
                            <article key={reporte.id} className={cardClassName}>
                                <div className="reporte-header-row">
                                    <div className="reporte-tags">
                                        <span className={`tag-pill ${tipoMeta.className}`}>{tipoMeta.label}</span>
                                        <span className={`status-pill ${estadoMeta.className}`}>{estadoMeta.label}</span>
                                    </div>
                                    {renderAdjuntos(reporte)}
                                </div>

                                <h3 className="reporte-title">{reporte.titulo || 'Reporte sin título'}</h3>
                                {reporte.descripcion && <p className="reporte-description">{reporte.descripcion}</p>}

                                <div className="reporte-meta">
                                    {reporte.ubicacion && (
                                        <span className="meta-item">
                                            <FaMapMarkerAlt />
                                            {reporte.ubicacion}
                                        </span>
                                    )}
                                    <span className="meta-item">
                                        <FaUser />
                                        {reporte.creadoPorNombre || reporte.nombreResidente || 'Residente'}
                                    </span>
                                </div>

                                <div className="reporte-footer">
                                    <span className="reporte-date">{formatearFechaCorta(reporte.fechaCreacion)}</span>
                                    <button
                                        type="button"
                                        className={`btn-toggle-read ${estaLeido ? 'mark-unread' : 'mark-read'}`}
                                        disabled={procesando}
                                        onClick={() => handleToggleLectura(reporte.id, estaLeido)}
                                    >
                                        {procesando ? (
                                            'Actualizando...'
                                        ) : (
                                            <>
                                                {estaLeido ? <FaEnvelopeOpen /> : <FaEnvelope />}
                                                {estaLeido ? 'Marcar como no leído' : 'Marcar como leído'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {evidenciasModal.open && evidenciasModal.reporte && (
                <div className="evidencias-modal-overlay" onClick={cerrarModalEvidencias}>
                    <div className="evidencias-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="evidencias-modal-header">
                            <h2>Evidencias - {evidenciasModal.reporte.titulo || 'Reporte'}</h2>
                            <button type="button" className="evidencias-modal-close" onClick={cerrarModalEvidencias}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="evidencias-modal-body">
                            {evidenciasModal.evidencias.fotos.length > 0 && (
                                <div className="evidencias-section">
                                    <h3>Fotos ({evidenciasModal.evidencias.fotos.length})</h3>
                                    <div className="evidencias-grid">
                                        {evidenciasModal.evidencias.fotos.map((url, index) => (
                                            <div key={index} className="evidencia-item">
                                                <img src={url} alt={`Foto ${index + 1}`} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {evidenciasModal.evidencias.videos.length > 0 && (
                                <div className="evidencias-section">
                                    <h3>Videos ({evidenciasModal.evidencias.videos.length})</h3>
                                    <div className="evidencias-grid">
                                        {evidenciasModal.evidencias.videos.map((url, index) => (
                                            <div key={index} className="evidencia-item">
                                                <video controls>
                                                    <source src={url} type="video/mp4" />
                                                    Tu navegador no soporta el elemento de video.
                                                </video>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {evidenciasModal.evidencias.audios.length > 0 && (
                                <div className="evidencias-section">
                                    <h3>Audios ({evidenciasModal.evidencias.audios.length})</h3>
                                    <div className="evidencias-audios">
                                        {evidenciasModal.evidencias.audios.map((url, index) => (
                                            <div key={index} className="evidencia-audio-item">
                                                <audio controls>
                                                    <source src={url} type="audio/mpeg" />
                                                    Tu navegador no soporta el elemento de audio.
                                                </audio>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropuestasIncidencias;

function extraerAdjuntos(reporte = {}) {
    const flags = { foto: false, video: false, audio: false };
    const adjuntos = reporte.adjuntos;

    if (Array.isArray(adjuntos)) {
        adjuntos.forEach((item) => {
            const key = String(item || '').toLowerCase();
            if (flags[key] !== undefined) {
                flags[key] = true;
            }
        });
    } else if (adjuntos && typeof adjuntos === 'object') {
        if (adjuntos.foto || adjuntos.fotos?.length) flags.foto = true;
        if (adjuntos.video || adjuntos.videos?.length) flags.video = true;
        if (adjuntos.audio || adjuntos.audios?.length) flags.audio = true;
    }

    if (reporte.foto || reporte.fotos?.length || reporte.fotoUrl) flags.foto = true;
    if (reporte.video || reporte.videos?.length || reporte.videoUrl) flags.video = true;
    if (reporte.audio || reporte.audios?.length || reporte.audioUrl) flags.audio = true;

    if (reporte.tieneFoto) flags.foto = true;
    if (reporte.tieneVideo) flags.video = true;
    if (reporte.tieneAudio) flags.audio = true;

    return flags;
}

function formatearFechaCorta(valor) {
    if (!valor) return '';
    const date = typeof valor.toDate === 'function' ? valor.toDate() : (valor instanceof Date ? valor : new Date(valor));
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}
